import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import { Progress, Card, CardBody } from 'reactstrap';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  ComposedChart,
  Text,
  LabelList
} from 'recharts';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import getTooltipStyles from '../../../../shared/helpers';
import Panel from '../../../../shared/components/Panel';
import StatIcon from 'mdi-react/MapMarkerOutlineIcon';
import LinearLoading from '../../../../shared/components/LinearLoading';

import {
  _capitalizeText,
  _formatNumber,
  _formatPrice,
  _getBuildingPhotos,
  _makeSlugifiedArr,
  _shouldShow,
  _urlToName,
  _getBuildingURL,
  _formatBigNumber
} from '../../../../shared/helpers/utils';
import {
  _axiosCall
} from '../../../../shared/helpers/apicalls';
import { getDefaultChartColors } from '../../../../shared/helpers';

function renderTotalLabel(props) {
  var x = props.x;
  var y = props.y;
  var width = props.width;
  var value = props.value;

  if (!value) {
	return null;
  }

  var label = value > 1000000
	? _formatBigNumber(value)
	: _formatNumber(value);

  return (
	<text
	  x={x + width / 2}
	  y={y - 5}
	  textAnchor="middle"
	  fill="#000"
	  dominantBaseline="middle"
	  fontWeight=""
	  fontSize={11}
	>
	  {label}
	</text>
  );
}




const colors = {
  condo_coop: getDefaultChartColors[0],   // #9a12b3
  rental: getDefaultChartColors[1],       // #00b5cc
  multi_family: getDefaultChartColors[2], // #f03434
  single_family: getDefaultChartColors[3] // #e9d460
};

const RES_TYPES = [
  { key: 'condo_coop', label: 'Condo / Coop' },
  { key: 'rental', label: 'Rental' },
  { key: 'multi_family', label: 'Multi-Family' },
  { key: 'single_family', label: 'Single-Family' }
];

const CustomizedAxisTick = function (props) {
  var x = props.x;
  var y = props.y;
  var payload = props.payload;

  return (
	<g transform={'translate(' + x + ',' + y + ')'}>
	  <text
		x={0}
		y={0}
		dy={16}
		textAnchor="end"
		fill="#666"
		transform="rotate(-45)"
	  >
		{_capitalizeText(payload.value)}
	  </text>
	</g>
  );
};

const svgToPng = function (svg, width, height) {
  return new Promise(function (resolve, reject) {
	var canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	var ctx = canvas.getContext('2d');

	ctx.fillStyle = '#ffffff';
	ctx.fillRect(0, 0, width, height);

	var xml = new XMLSerializer().serializeToString(svg);
	var dataUrl = 'data:image/svg+xml;utf8,' + encodeURIComponent(xml);
	var img = new Image(width, height);

	img.onload = function () {
	  ctx.drawImage(img, 0, 0);
	  var imageData = canvas.toDataURL('image/png', 1.0);
	  resolve(imageData);
	};

	img.onerror = function () {
	  reject();
	};

	img.src = dataUrl;
  });
};

class NeighborhoodResiTypeAnalysis extends PureComponent {
  constructor(props) {
	super(props);
	this.state = {
	  fetchInProgress: false,
	  data: null,
	  period: '1-day'
	};

	this.handleClick = this.handleClick.bind(this);
	this.handleDownload = this.handleDownload.bind(this);
	this._constructNeighborhoodData = this._constructNeighborhoodData.bind(this);
  }

  currentChart;

  _constructNeighborhoodData(facetData) {
	var analysisType = this.props.analysisType;

	var neighborhoods = [];
	if (facetData) {
	  if (facetData.resi_by_neighborhood && facetData.resi_by_neighborhood.length) {
		neighborhoods = facetData.resi_by_neighborhood;
	  } else if (facetData.by_neighborhood && facetData.by_neighborhood.length) {
		neighborhoods = facetData.by_neighborhood;
	  }
	}

	var data = [];

	neighborhoods.forEach(function (hood) {
	  var hoodNameArr = hood._id && hood._id.neighborhood ? hood._id.neighborhood : [];
	  var hoodName = hoodNameArr && hoodNameArr.length ? hoodNameArr[0] : '';
	  var borough = hood._id && hood._id.borough ? hood._id.borough : '';

	  var hoodData = {
		name: hoodName,
		search:
		  hoodName.toLowerCase().replace(/ /g, '-') +
		  '_' +
		  String(borough).toLowerCase().replace(/ /g, '-'),
		_total: 0
	  };

	  if (hood.types && hood.types.length) {
		hood.types.forEach(function (t) {
		  var key = t.res_type;
		  if (!key) {
			return;
		  }

		  var v = 0;
		  if (analysisType === 'count') {
			v = t.count || 0;
		  } else if (analysisType === 'units') {
			v = t.units || 0;
		  } else if (analysisType === 'sq_ft') {
			v = t.sq_ft || 0;
		  }

		  hoodData[key] = (hoodData[key] || 0) + v;
		  hoodData._total += v;
		});
	  } else {
		// Fallback for older flat data
		var total = 0;
		if (analysisType === 'count') {
		  total = hood.count || hood.total_count || 0;
		} else if (analysisType === 'units') {
		  total = hood.units || hood.total_units || 0;
		} else if (analysisType === 'sq_ft') {
		  total = hood.sq_ft || hood.total_sq_ft || 0;
		}
		hoodData.total = total;
		hoodData._total = total;
	  }

	  data.push(hoodData);
	});

	data.sort(function (a, b) {
	  return (b._total || 0) - (a._total || 0);
	});

	return data;
  }

  handleClick(data, index) {
	var baseURL = this.props.baseURL;
	window.location = baseURL + '&neighborhood=' + data.search;
  }

  async handleDownload() {
	return false;
	// If you want this later, remove the return above.
	var chart = this.currentChart;
	var chartSVG = ReactDOM.findDOMNode(chart).children[0];

	var pngData = await svgToPng(chartSVG, 1200, 1600);
	document.write('<img src="' + pngData + '"/>');
  }

  render() {

	var period = this.state.period;
	const { facetData, classes, analysisType, buildingsFetchInProgress, fetchInProgress  } = this.props;    


	if (!facetData) {
	  return null;
	}

	var data = buildingsFetchInProgress ? [] : this._constructNeighborhoodData(facetData);

	var title = {
	  count: 'Top Neighborhoods by Number of Projects',
	  units: 'Top Neighborhoods by Number of Residential Units',
	  sq_ft: 'Top Neighborhoods by Total Residential Square Feet'
	};

	var activeResTypes = RES_TYPES;
	if (data && data.length) {
	  activeResTypes = RES_TYPES.filter(function (t) {
		var found = false;
		for (var i = 0; i < data.length; i += 1) {
		  if (data[i][t.key]) {
			found = true;
			break;
		  }
		}
		return found;
	  });
	}

	return (
	  <Card>
		<CardBody className="dashboard__booking-card">
		  <div className="dashboard__booking-total-container">
			<h5 className="dashboard__booking-total-description">
			  {title[this.props.analysisType]}
			</h5>
			<StatIcon
			  className="dashboard__trend-icon"
			  onClick={this.handleDownload}
			/>
		  </div>
		  {(!facetData || fetchInProgress || buildingsFetchInProgress) ?
			<div>
				<LinearLoading />
				<div style={{ height: 360 }}>
			
				</div>
			</div>
			
			:
		  <ResponsiveContainer height={400} className="dashboard__area">
			<BarChart
			  ref={function (chart) {
				this.currentChart = chart;
			  }.bind(this)}
			  data={data}
			  margin={{ top: 50, left: -20, bottom: 20, right: 20 }}
			>
			  <XAxis
				dataKey="name"
				interval={0}
				tickLine={false}
				reversed={false}
				tick={<CustomizedAxisTick />}
				height={60}
			  />
			  <YAxis
				yAxisId="left"
				tickLine={true}
				orientation="left"
				tickFormatter={function (value) {
				  return new Intl.NumberFormat('en').format(value);
				}}
			  />
			  <Tooltip
				formatter={function (value, name, props) {
				  return _formatNumber(value);
				}}
				labelFormatter={function (value) {
				  return _capitalizeText(value);
				}}
				cursor={{ stroke: '#780F9E', strokeWidth: 1 }}
			  />
			  <Legend />
			  <CartesianGrid vertical={false} />

			 {activeResTypes && activeResTypes.length ? (
			   activeResTypes.map(function (t, idx) {
				 var isTopSeries = (idx === activeResTypes.length - 1);
			 
				 return (
				   <Bar
					 key={t.key}
					 dataKey={t.key}
					 name={t.label}
					 yAxisId="left"
					 stackId="a"
					 barSize={35}
					 fill={colors[t.key]}
					 onClick={this.handleClick}
					 cursor="pointer"
				   >
					 {isTopSeries ? (
					   <LabelList
						 dataKey="_total"        // use the total value
						 content={renderTotalLabel}
					   />
					 ) : null}
				   </Bar>
				 );
			   }.bind(this))
			 ) : (
			   <Bar
				 dataKey="total"
				 yAxisId="left"
				 stackId="a"
				 barSize={35}
				 fill={colors.condo_coop}
				 onClick={this.handleClick}
				 cursor="pointer"
			   >
				 <LabelList
				   dataKey="total"
				   content={renderTotalLabel}
				 />
			   </Bar>
			 )}



			</BarChart>
		  </ResponsiveContainer>}
		</CardBody>
	  </Card>
	);
  }
}

export default NeighborhoodResiTypeAnalysis;
